
'use client';

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

type Consent = {
  advertising: boolean;
};

type CookieConsentContextType = {
  consent: Consent;
  hasMadeChoice: boolean;
  isSettingsOpen: boolean;
  acceptAll: () => void;
  declineAll: () => void;
  savePreferences: (preferences: Consent) => void;
  openSettings: () => void;
  closeSettings: () => void;
};

const defaultConsent: Consent = {
  advertising: false,
};

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const CookieConsentProvider = ({ children }: { children: ReactNode }) => {
  const [consent, setConsent] = useState<Consent>(defaultConsent);
  const [hasMadeChoice, setHasMadeChoice] = useState(true); // Assume choice made until checked
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  useEffect(() => {
    // This code only runs on the client
    try {
      const storedConsent = localStorage.getItem('cookie_consent');
      if (storedConsent) {
        setConsent(JSON.parse(storedConsent));
        setHasMadeChoice(true);
      } else {
        setHasMadeChoice(false);
      }
    } catch (error) {
        console.error("Failed to parse cookie consent from localStorage", error);
        setHasMadeChoice(false);
    }
  }, []);

  const updateConsent = useCallback((newConsent: Consent) => {
    try {
      localStorage.setItem('cookie_consent', JSON.stringify(newConsent));
      setConsent(newConsent);
      setHasMadeChoice(true);
    } catch (error) {
        console.error("Failed to save cookie consent to localStorage", error);
    }
  }, []);

  const acceptAll = useCallback(() => {
    const allConsent: Consent = { advertising: true };
    updateConsent(allConsent);
  }, [updateConsent]);

  const declineAll = useCallback(() => {
    const noConsent: Consent = { advertising: false };
    updateConsent(noConsent);
  }, [updateConsent]);

  const savePreferences = useCallback((preferences: Consent) => {
    updateConsent(preferences);
  }, [updateConsent]);
  
  const openSettings = useCallback(() => {
    setIsSettingsOpen(true);
  }, []);

  const closeSettings = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);

  return (
    <CookieConsentContext.Provider value={{ consent, hasMadeChoice, isSettingsOpen, acceptAll, declineAll, savePreferences, openSettings, closeSettings }}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};
